/**
 * Batch Home Data Service
 *
 * This service batches all home page API requests into a single parallel fetch
 * to improve initial page load performance.
 *
 * Benefits:
 * - Reduces waterfall requests (6 separate → 1 parallel batch)
 * - Improves Time to Interactive (TTI)
 * - Better user experience with faster load times
 */

import { homeService } from './index';
import { challengeService } from '../challengeService/challengeService';
import { postsService } from '../postsService/postsService';
import { Banner } from '@/app/models/banner/BannerModel';
import ChallengeModel from '@/app/models/challenges/ChallengeModel';
import { PostModel } from '@/app/models/posts/Posts';

export interface BatchHomeData {
    banners: Banner[];
    challenges: ChallengeModel[];
    news: PostModel[];
    featuredPosts: PostModel[];
    newestPosts: PostModel[];
    products: PostModel[];
    faqs: any[];
}

/**
 * Fetches all home page data in parallel
 * This reduces the number of sequential API calls and improves performance
 *
 * @param signal - AbortSignal for request cancellation
 * @param ownerId - User ID to filter hidden posts
 * @returns Promise with all home page data
 */
export async function fetchBatchHomeData(signal?: AbortSignal, ownerId?: string | number): Promise<BatchHomeData> {
    try {
        // Execute all API calls in parallel using Promise.all
        const [
            bannersResponse,
            challengesResponse,
            newsResponse,
            featuredPostsResponse,
            newestPostsResponse,
            productsResponse,
            faqsResponse
        ] = await Promise.all([
            // Banners
            homeService.getBanners({
                page: 'home',
                position: 'top',
                signal
            }).catch(error => ({ data: [] })),

            // Challenges
            challengeService.GetListChallenges({
                perPage: 1,
                condition: 'common',
                is_show_home: 1,
                signal
            }).catch(error => ({ data: [] })),

            // News
            postsService.getPostLists({
                type: 'news',
                is_show_home: 1,
                perPage: 2,
                signal
            }).catch(error => ({ data: [] })),

            // Featured Posts
            homeService.getTopPosts({
                perPage: 4,
                signal,
                ownerId
            }).catch(error => ({ data: [] })),

            postsService.getPostLists({
                type: 'post',
                perPage: 4,
                signal,
                ownerId
            }).catch(error => ({ data: [] })),

            // Products
            postsService.getPostLists({
                type: 'product',
                perPage: 4,
                signal,
                ownerId
            }).catch(error => ({ data: [] })),

            // FAQs
            homeService.getFaqHome(signal as AbortSignal).catch(error => ({ data: [] }))
        ]);

        // Transform data into models
        return {
            banners: (bannersResponse.data || []).map((item: any) => new Banner(item)),
            challenges: (challengesResponse.data || []).map((item: any) => new ChallengeModel(item)),
            news: (newsResponse.data || []).map((item: any) => new PostModel(item.post || item)),
            featuredPosts: (featuredPostsResponse.data || []).map((item: any) => new PostModel(item.post || item)),
            newestPosts: (newestPostsResponse.data || []).map((item: any) => new PostModel(item.post || item)),
            products: (productsResponse.data || []).map((item: any) => new PostModel(item.post || item)),
            faqs: faqsResponse.data || []
        };
    } catch (error) {
        // If the entire batch fails, throw the error
        throw error;
    }
}


/**
 * Fetch only featured posts
 * @param signal - AbortSignal for request cancellation
 * @param ownerId - User ID to filter hidden posts
 */
export async function fetchFeaturedPosts(signal?: AbortSignal, ownerId?: string | number): Promise<PostModel[]> {
    try {
        const response = await homeService.getTopPosts({
            perPage: 4,
            signal: signal || undefined, // Don't pass null/undefined signal on iOS
            ownerId // IMPORTANT: Pass ownerId to filter hidden posts
        });
        return (response.data || []).map((item: any) => new PostModel(item.post || item));
    } catch (error) {
        console.error('[fetchFeaturedPosts] Error:', error);
        // Don't swallow error - let caller handle it
        throw error;
    }
}

/**
 * Fetch only newest posts (sorted by created_at desc)
 * @param signal - AbortSignal for request cancellation
 */
export async function fetchNewestPosts(signal?: AbortSignal, ownerId?: string | number): Promise<PostModel[]> {
    try {
        const response = await postsService.getPostLists({
            type: 'post',
            perPage: 4,
            signal: signal || undefined, // Don't pass null/undefined signal on iOS
            ownerId
        });
        return (response.data || []).map((item: any) => new PostModel(item.post || item));
    } catch (error) {
        console.error('[fetchNewestPosts] Error:', error);
        // Don't swallow error - let caller handle it
        throw error;
    }
}

/**
 * Fetch only products
 */
export async function fetchProducts(signal?: AbortSignal, ownerId?: string | number): Promise<PostModel[]> {
    try {
        const response = await postsService.getPostLists({
            type: 'product',
            perPage: 4,
            signal: signal || undefined,
            ownerId
        });
        return (response.data || []).map((item: any) => new PostModel(item.post || item));
    } catch (error) {
        console.error('[fetchProducts] Error:', error);
        // Don't swallow error - let caller handle it
        throw error;
    }
}
